  import React from "react";
  import PropTypes from "prop-types";
  import Box from "@material-ui/core/Box";
  import Button from "@material-ui/core/Button";
  import CircularProgress from "@material-ui/core/CircularProgress";
  import TextField from "@material-ui/core/TextField";
  import InputAdornment from "@material-ui/core/InputAdornment";
  import Typography from "@material-ui/core/Typography";
  import IconButton from "@material-ui/core/IconButton";
  import EditIcon from "@material-ui/icons/Edit";
  import { makeStyles } from "@material-ui/core/styles";
  import {
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
  } from "@mui/material";
  import {
    validateField,
    validateMerchantRow,
    validateAllMerchantRows,
  } from "./nachaMerchantTableValidation";
  import { post } from "../../actions/httpClient";
  import * as urls from "../../Uintls/Urls";

  const useStyles = makeStyles((theme) => ({
    section: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
    titleBar: {
      alignItems: "center",
      backgroundColor: theme.palette.primary.main,
      borderTopLeftRadius: "4px",
      borderTopRightRadius: "4px",
      color: theme.palette.primary.contrastText,
      display: "flex",
      height: "40px",
      padding: theme.spacing(0, 2),
    },
    tableWrapper: {
      marginTop: 0,
      overflowX: "auto",
      maxWidth: "100%",
    },
    table: {
      minWidth: 900,
    },
    headerCell: {
      backgroundColor: theme.palette.grey[100],
      borderBottom: `1px solid ${theme.palette.divider}`,
      color: theme.palette.text.primary,
      fontSize: "0.8rem",
      fontWeight: 600,
      padding: theme.spacing(1.25, 1.5),
      whiteSpace: "nowrap",
      verticalAlign: "top",
    },
    bodyCell: {
      minHeight: 72,
      padding: theme.spacing(1.25, 1.5),
      verticalAlign: "top",
      whiteSpace: "nowrap",
    },
    cellContainer: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "flex-start",
      alignItems: "stretch",
      height: "100%",
    },
    cellContent: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      alignItems: "stretch",
      height: "100%",
    },
    helperSpace: {
      minHeight: 20,
    },
    input: {
      width: "100%",
      boxSizing: "border-box",
      "& .MuiOutlinedInput-root": {
        borderRadius: "4px",
        backgroundColor: theme.palette.common.white,
      },
      "& .MuiInputBase-input": {
        fontSize: "0.875rem",
        padding: "8px 10px",
      },
    },
    inputAdornment: {
      marginRight: 0,
      fontWeight: 600,
      whiteSpace: "nowrap",
    },
    sftpInput: {
      "& .MuiInputBase-input": {
        paddingLeft: 0,
      },
    },
    readOnlyText: {
      color: theme.palette.text.primary,
      fontSize: "0.875rem",
      fontWeight: 500,
    },
    actionCell: {
      minWidth: "150px",
      padding: theme.spacing(1.25, 1.5),
      width: "150px",
      verticalAlign: "top",
    },
    actionButtonGroup: {
      alignItems: "center",
      display: "flex",
      gap: theme.spacing(0.5),
      justifyContent: "flex-end",
    },
    actionButton: {
      minWidth: 70,
      whiteSpace: "nowrap",
    },
    actionRow: {
      alignItems: "center",
      display: "flex",
      gap: theme.spacing(1),
      justifyContent: "flex-end",
      marginTop: theme.spacing(2),
      paddingRight: theme.spacing(1),
    },
    paginationRow: {
      alignItems: "center",
      display: "flex",
      justifyContent: "space-between",
      marginTop: theme.spacing(1.5),
      padding: theme.spacing(0, 1),
    },
    paginationInfo: {
      color: theme.palette.text.secondary,
      fontSize: "0.875rem",
    },
    paginationButtons: {
      display: "flex",
      gap: theme.spacing(1),
      alignItems: "center",
    },
    rowsPerPageSelect: {
      minWidth: 90,
    },
    progress: {
      color: theme.palette.primary.contrastText,
      marginRight: theme.spacing(1),
    },
    message: {
      marginTop: theme.spacing(2),
      padding: theme.spacing(2),
      textAlign: "center",
    },
  }));

  const nachaColumns = [
    {
      key: "apiMerchantCode",
      header: ["API", "Code"],
      aliases: ["apiMerchantCode", "api_merchant_code", "APIMerchantCode"],
    },
    {
      key: "nachaMerchantCode",
      header: ["NACHA", "Code"],
      aliases: ["nachaMerchantCode", "nacha_merchant_code", "NACHAMerchantCode"],
    },
    {
      key: "accountType",
      header: ["Account", "Type"],
      aliases: ["accountType"],
    },
    {
      key: "merchantName",
      header: ["Merchant", "Name"],
      aliases: ["merchantName", "merchant_name", "MerchantName"],
    },
    {
      key: "merchantTollNumber",
      header: ["Toll", "Number"],
      aliases: [
        "merchantTollNumber",
        "merchant_toll_number",
        "TollNumber",
        "tollNumber",
      ],
    },
    {
      key: "merchantIdentificationNumber",
      header: ["Merchant", "Identification"],
      aliases: [
        "merchantIdentificationNumber",
        "merchant_identification_number",
        "MerchantIdentificationNumber",
        "merchantIdNumber",
        "merchantId",
      ],
    },
    {
      key: "merchantTrustAccountNumber",
      header: ["Trust", "Account"],
      aliases: [
        "merchantTrustAccountNumber",
        "merchant_trust_account_number",
        "MerchantTrustAccountNumber",
        "trustAccountNumber",
        "trustAccountNo",
      ],
    },
    {
      key: "sftpFileLocation",
      header: ["SFTP", "Location"],
      aliases: ["sftpFileLocation", "sftp_file_location", "SFTPFileLocation"],
    },
  ];

  const SFTP_UPLOAD_PREFIX = "/sftpuser/uploads/";

const getColumnValue = (row, column) => {
    const foundKey = column.aliases.find((key) => row[key] !== undefined);
    const value = foundKey ? row[foundKey] : row[column.key];
    return value === undefined || value === null ? "" : value;
  };

const getSftpSuffix = (value) => {
    if (!value) {
      return "";
    }
    if (typeof value !== "string") {
      return String(value);
    }
    return value.startsWith(SFTP_UPLOAD_PREFIX)
      ? value.slice(SFTP_UPLOAD_PREFIX.length)
      : value;
  };

const getFullSftpPath = (suffix) => {
    if (!suffix || suffix === "") {
      return "";
    }
    return `${SFTP_UPLOAD_PREFIX}${suffix}`;
  };

const getWritableKey = (row, column) =>
    column.aliases.find((key) => row[key] !== undefined) || column.key;

  const readOnlyColumns = ["nachaMerchantCode", "accountType"];
  const editableFieldKeys = [
    "apiMerchantCode",
    "merchantName",
    "merchantTollNumber",
    "merchantIdentificationNumber",
    "merchantTrustAccountNumber",
    "sftpFileLocation",
  ];

  const createEditableMap = (rows) =>
    rows.map(() =>
      nachaColumns.reduce((editable, column) => {
        editable[column.key] = editableFieldKeys.includes(column.key);
        return editable;
      }, {}));

  const getRowKey = (row, rowIndex) => row._id || row.id || `row-${rowIndex}`;

    const RowCell = ({ className = "", classes, children }) => (
    <TableCell className={`${classes.bodyCell}${className ? ` ${className}` : ""}`}>
      <Box className={classes.cellContainer}>{children}</Box>
    </TableCell>
  );

  const CellContent = ({ classes, children }) => (
    <Box className={classes.cellContent}>{children}</Box>
  );

  const NachaMerchantTableRow = React.memo(
    function NachaMerchantTableRow({
      row,
      rowIndex,
      columns,
      editableMap,
      rowErrors,
      isEditing,
      onEdit,
      onSave,
      onCancel,
      onChange,
      onBlur,
      rowValidation,
      readOnlyColumns,
      touchedFields,
      classes,
    }) {
      const rowKey = getRowKey(row, rowIndex);

      return (
        <TableRow>
          <TableCell className={classes.bodyCell}>{rowIndex + 1}</TableCell>
            {columns.map((column) => {
            const value = getColumnValue(row, column);
            const isReadOnly = readOnlyColumns.includes(column.key);
            const isTouched = Boolean(touchedFields?.[column.key]);
            const error = rowErrors[column.key] && (isTouched || isEditing)
              ? rowErrors[column.key]
              : "";

            const placeholders = {
              apiMerchantCode: "Example: STBT123456",
              merchantName: "Merchant Name",
              merchantTollNumber: "9876543210",
              merchantIdentificationNumber: "Example: 1234567890",
              merchantTrustAccountNumber: "12345678901234567890",
              sftpFileLocation: "/inbound/nacha/company1",
            };

            return (
              <RowCell classes={classes} key={column.key}>
                <CellContent classes={classes}>
                  {isEditing && !isReadOnly ? (
                    <TextField
                      className={
                        column.key === "sftpFileLocation"
                          ? `${classes.input} ${classes.sftpInput}`
                          : classes.input
                      }
                      error={
                        !!error ||
                        (column.key === "sftpFileLocation" && rowValidation?.valid === false)
                      }
                      helperText={
                        column.key === "sftpFileLocation"
                          ? error || (rowValidation?.valid === false ? rowValidation.message : " ")
                          : error || " "
                      }
                      inputProps={{
                        "data-focus-row": rowIndex,
                        "data-focus-field": column.key,
                      }}
                      InputProps={
                        column.key === "sftpFileLocation"
                          ? {
                              startAdornment: (
                                <InputAdornment
                                  position="start"
                                  className={classes.inputAdornment}
                                >
                                  {SFTP_UPLOAD_PREFIX}
                                </InputAdornment>
                              ),
                              endAdornment: (
                                <InputAdornment position="end">
                                  {rowValidation?.loading ? (
                                    <CircularProgress size={16} thickness={5} />
                                  ) : rowValidation?.valid ? (
                                    <Typography style={{ color: "green" }}>✔</Typography>
                                  ) : rowValidation?.valid === false ? (
                                    <Typography style={{ color: "red" }}>✖</Typography>
                                  ) : null}
                                </InputAdornment>
                              ),
                            }
                          : undefined
                      }
                      onChange={(event) =>
                        onChange(rowIndex, column, event.target.value)
                      }
                      onBlur={() =>
                        column.key === "sftpFileLocation" &&
                        onBlur &&
                        onBlur(rowIndex)
                      }
                      placeholder={
                        column.key === "sftpFileLocation"
                          ? ""
                          : placeholders[column.key] || ""
                      }
                      size="small"
                      value={value}
                      variant="outlined"
                      fullWidth
                    />
                  ) : (
                    <Typography className={classes.readOnlyText}>
                      {value}
                    </Typography>
                  )}
                  {(!isEditing || isReadOnly) && (
                    <Box className={classes.helperSpace}>&nbsp;</Box>
                  )}
                </CellContent>
                
              </RowCell>
            );
          })}
          <RowCell className={classes.actionCell} classes={classes}>
            
            <CellContent classes={classes}>
              <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                {/* Validation status indicator
                    {(() => {
                      const status = rowValidation;
                      if (!status) return null;
                      if (status.loading) {
                        return (
                          <Box ml={1} display="flex" alignItems="center">
                            <CircularProgress size={16} thickness={5} />
                          </Box>
                        );
                      }
                      if (status.valid) {
                        return (
                          <Typography style={{ color: "green", marginLeft: -18, marginBottom: 24 }}>✔</Typography>
                        );
                      }
                      return (
                        <Typography style={{ color: "red", marginLeft: -18, marginBottom: 24 }}>✖</Typography>
                      );
                    })()} */}
                      {isEditing ? (
                  <div style={{ marginBottom: "24px" }}>
                    <Button
                      className={classes.actionButton}
                      color="primary"
                      style={{ marginRight: "8px" }}
                      onClick={() => onSave(rowIndex, rowKey)}
                      size="small"
                      variant="contained"
                    >
                      Save
                    </Button>
                    <Button
                      className={classes.actionButton}
                      onClick={() => onCancel(rowIndex, rowKey)}
                      size="small"
                      variant="outlined"
                    >
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <IconButton
                    onClick={() => onEdit(rowIndex, rowKey, row)}
                    size="small"
                  >
                    <EditIcon />
                  </IconButton>
                )}
                  
              </Stack>
            </CellContent>
          </RowCell>
        </TableRow>
      );
    },
    (prevProps, nextProps) =>
      prevProps.row === nextProps.row &&
      prevProps.isEditing === nextProps.isEditing &&
      prevProps.rowErrors === nextProps.rowErrors &&
      prevProps.editableMap === nextProps.editableMap &&
      prevProps.rowValidation === nextProps.rowValidation &&
      prevProps.touchedFields === nextProps.touchedFields
  );

  function NachaMerchantTable({
    canRetry,
    rows,
    loading,
    saving,
    onRetry,
    onSave,
    merchantId,
    saveLabel,
    title,
    message,
  }) {
    const classes = useStyles();
    const [pageSize, setPageSize] = React.useState(10);
    const [editableRows, setEditableRows] = React.useState([]);
    const [editableMap, setEditableMap] = React.useState([]);
    const [rowErrors, setRowErrors] = React.useState({});
    const [touchedFields, setTouchedFields] = React.useState({});
    const [editingRows, setEditingRows] = React.useState({});
    const [rowValidationStatus, setRowValidationStatus] = React.useState({});
    const [validatingAll, setValidatingAll] = React.useState(false);
    const validationPromisesRef = React.useRef({});
    const [page, setPage] = React.useState(1);
    const [focusTarget, setFocusTarget] = React.useState(null);
    const editingOriginalRef = React.useRef({});

    React.useEffect(() => {
      setEditableRows(
        rows.map((row) => ({
          ...row,
          sftpFileLocation: getSftpSuffix(
            getColumnValue(row, {
              key: "sftpFileLocation",
              aliases: ["sftpFileLocation", "sftp_file_location", "SFTPFileLocation"],
            })
          ),
        }))
      );
      setEditableMap(createEditableMap(rows));
      setRowErrors({});
      setTouchedFields({});
      setEditingRows({});
      setPage(1);
      editingOriginalRef.current = {};
    }, [rows]);

    React.useEffect(() => {
      setPage(1);
    }, [pageSize]);

    React.useEffect(() => {
      if (!focusTarget) {
        return;
      }

      const selector = `[data-focus-row="${focusTarget.rowIndex}"][data-focus-field="${focusTarget.fieldKey}"]`;
      const target = document.querySelector(selector);

      if (target) {
        target.scrollIntoView({ block: "center", inline: "nearest" });
        target.focus();
      }

      setFocusTarget(null);
    }, [focusTarget, page]);

    const handleChange = React.useCallback((rowIndex, column, value) => {
      const nextValue =
        column.key === "sftpFileLocation" ? getSftpSuffix(value) : value;

      setEditableRows((currentRows) => {
        const nextRows = [...currentRows];
        const nextRow = { ...nextRows[rowIndex] };
        const writableKey = getWritableKey(nextRow, column);
        nextRow[writableKey] = nextValue;
        nextRow[column.key] = nextValue;
        nextRows[rowIndex] = nextRow;
        return nextRows;
      });

      setTouchedFields((currentTouched) => ({
        ...currentTouched,
        [rowIndex]: {
          ...(currentTouched[rowIndex] || {}),
          [column.key]: true,
        },
      }));

      // If user edits SFTP field, clear previous validation state for that row
      if (column.key === "sftpFileLocation") {
        setRowValidationStatus((current) => {
          const next = { ...current };
          delete next[rowIndex];
          return next;
        });
      }

      setRowErrors((currentErrors) => {
        const nextErrors = { ...(currentErrors[rowIndex] || {}) };
        nextErrors[column.key] = validateField(column.key, value);
        return {
          ...currentErrors,
          [rowIndex]: nextErrors,
        };
      });
    }, []);

    const handleEditClick = React.useCallback((rowIndex, rowKey, row) => {
      editingOriginalRef.current[rowKey] = { ...row };
      setRowErrors((currentErrors) => {
        const nextErrors = { ...currentErrors };
        delete nextErrors[rowIndex];
        return nextErrors;
      });
      setTouchedFields((currentTouched) => {
        const nextTouched = { ...currentTouched };
        delete nextTouched[rowIndex];
        return nextTouched;
      });
      setEditingRows((current) => ({ ...current, [rowKey]: true }));
    }, []);

    const parseBooleanResponse = (value) => {
      if (value === true || value === "true" || value === 1 || value === "1") {
        return true;
      }
      if (value === false || value === "false" || value === 0 || value === "0") {
        return false;
      }
      return undefined;
    };

    // Validate a single row's SFTP path by calling the backend once.
    // This function centralizes the validation lifecycle (validating -> success|failed)
    // and always clears the loading flag in a finally block.
    const validateRowSftp = React.useCallback(
      async (rowIndex) => {
        const row = editableRows[rowIndex];
        if (!row) return false;

        if (!merchantId) {
          setRowValidationStatus((current) => ({
            ...current,
            [rowIndex]: { loading: false, valid: false, message: "Missing merchantId" },
          }));
          return false;
        }

        // mark loading
        setRowValidationStatus((current) => ({
          ...current,
          [rowIndex]: { ...(current[rowIndex] || {}), loading: true, valid: null, message: "" },
        }));

        // Get SFTP location from row
        const sftpLocation = row.sftpFileLocation || row.sftp_file_location || row.SFTPFileLocation || "";
        const payload = {
          filePath: `/sftpuser/uploads/${sftpLocation}`,
        };

        let isValid = false;
        let message = "Validation failed";

        // reuse an in-flight validation promise to avoid duplicate API calls
        if (validationPromisesRef.current[rowIndex]) {
          return validationPromisesRef.current[rowIndex];
        }

        const promise = (async () => {
          try {
            const res = await post(urls.VALIDATE_NACHA_SFTP_FILE_URL, payload);

            const apiStatus = parseBooleanResponse(res?.status);
            const successCode = parseBooleanResponse(res?.data?.IsSuccessStatusCode);
            isValid = apiStatus === true || successCode === true;
            message =
              res?.message ||
              res?.data?.result ||
              res?.data?.message ||
              (isValid ? "Folder exists." : "Folder does not exist.");
          } catch (err) {
            // The http client may throw the response object for business failures.
            const apiStatus = parseBooleanResponse(err?.status);
            const successCode = parseBooleanResponse(err?.data?.IsSuccessStatusCode);
            isValid = apiStatus === true || successCode === true;
            message =
              err?.message ||
              err?.data?.result ||
              err?.data?.message ||
              message;
          } finally {
            // ensure loading is cleared for success, business-failure, exception, or network error
            setRowValidationStatus((current) => ({
              ...current,
              [rowIndex]: { loading: false, valid: isValid, message, lastValidatedValue: payload.filePath },
            }));
            delete validationPromisesRef.current[rowIndex];
          }

          return isValid;
        })();

        validationPromisesRef.current[rowIndex] = promise;
        return promise;
      },
      [editableRows, merchantId]
    );

    const handleRowCancel = React.useCallback((rowIndex, rowKey) => {
      const original = editingOriginalRef.current[rowKey];
      if (original) {
        setEditableRows((currentRows) => {
          const next = [...currentRows];
          next[rowIndex] = { ...original };
          return next;
        });
      }
      setRowErrors((currentErrors) => {
        const nextErrors = { ...currentErrors };
        delete nextErrors[rowIndex];
        return nextErrors;
      });
      setTouchedFields((currentTouched) => {
        const nextTouched = { ...currentTouched };
        delete nextTouched[rowIndex];
        return nextTouched;
      });
      setEditingRows((current) => {
        const next = { ...current };
        delete next[rowKey];
        return next;
      });
      delete editingOriginalRef.current[rowKey];
    }, []);

    const handleRowSave = React.useCallback((rowIndex, rowKey) => {
      const row = editableRows[rowIndex];
      const { isValid, errors } = validateMerchantRow(row);

      setRowErrors((currentErrors) => ({
        ...currentErrors,
        [rowIndex]: errors,
      }));

      if (!isValid) {
        return;
      }

      setEditingRows((current) => {
        const next = { ...current };
        delete next[rowKey];
        return next;
      });
      delete editingOriginalRef.current[rowKey];
    }, [editableRows]);

    const validateAllRows = React.useCallback(() => {
      const { isValid, rowErrors, invalidRows } = validateAllMerchantRows(
        editableRows
      );

      setRowErrors(rowErrors);

      if (!isValid) {
        setEditingRows((current) => {
          const next = { ...current };
          invalidRows.forEach((rowIndex) => {
            const rowKey = getRowKey(editableRows[rowIndex], rowIndex);
            next[rowKey] = true;
          });
          return next;
        });
      }

      return { isValid, rowErrors, invalidRows };
    }, [editableRows]);

    const isSftpValidationPending = React.useMemo(
      () =>
        Object.values(rowValidationStatus).some(
          (status) => status?.loading === true
        ),
      [rowValidationStatus]
    );

    const isSftpValidationFailed = React.useMemo(
      () =>
        Object.values(rowValidationStatus).some(
          (status) => status?.valid === false
        ),
      [rowValidationStatus]
    );

    const isSftpValidationIncomplete = React.useMemo(
      () => editableRows.some((_, index) => !rowValidationStatus[index]),
      [editableRows, rowValidationStatus]
    );

    const canSaveRows = React.useMemo(
      () =>
        editableRows.length > 0 &&
        !isSftpValidationPending &&
        !isSftpValidationFailed &&
        !isSftpValidationIncomplete,
      [editableRows.length, isSftpValidationPending, isSftpValidationFailed, isSftpValidationIncomplete]
    );

    const handleSave = React.useCallback(async () => {
      const { isValid, rowErrors, invalidRows } = validateAllRows();

      if (!isValid) {
        const firstInvalidRow = invalidRows[0];
        const fieldKey = Object.keys(rowErrors[firstInvalidRow] || [])[0];
        const invalidPage = Math.floor(firstInvalidRow / pageSize) + 1;

        setPage(invalidPage);
        setFocusTarget({ rowIndex: firstInvalidRow, fieldKey });
        return;
      }

      // Run SFTP validations exactly once for this Save attempt.
      // Skip validation for rows whose current value already validated successfully.
      // Block save immediately if any row has a failed validation for the current value.
      setValidatingAll(true);
      try {
        const validationTasks = [];

        for (let idx = 0; idx < editableRows.length; idx += 1) {
          const r = editableRows[idx];
          const currentFull = getFullSftpPath(getSftpSuffix(r.sftpFileLocation));
          const prev = rowValidationStatus[idx];

          if (prev && prev.lastValidatedValue === currentFull) {
            // already validated for this value
            if (prev.valid === false) {
              // blocked — do not proceed
              const fieldKey = "sftpFileLocation";
              const invalidPage = Math.floor(idx / pageSize) + 1;
              setPage(invalidPage);
              setFocusTarget({ rowIndex: idx, fieldKey });
              return;
            }
            // previously valid: skip validation
            validationTasks.push(Promise.resolve(true));
          } else {
            // need to validate this row
            validationTasks.push(validateRowSftp(idx));
          }
        }

        const results = await Promise.all(validationTasks);
        const allValid = results.every(Boolean);
        if (!allValid) {
          // Do not proceed to save; failure state/icons are set per row by validateRowSftp.
          return;
        }

        const rowsToSave = editableRows.map((row) => ({
          ...row,
          sftpFileLocation: getFullSftpPath(getSftpSuffix(row.sftpFileLocation)),
        }));

        onSave(rowsToSave);
      } finally {
        setValidatingAll(false);
      }
    }, [editableRows, pageSize, validateAllRows, validateRowSftp, onSave]);

    const hasRows = editableRows.length > 0;
    const totalPages = Math.max(1, Math.ceil(editableRows.length / pageSize));
    const safePage = Math.min(page, totalPages);
    const startIndex = (safePage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const pagedRows = editableRows.slice(startIndex, endIndex);
    const emptyMessage = message || "No merchant configuration found.";

    // If any row has already been validated for its current value and failed,
    // block Save and keep the error visible.
    const hasValidationFailedNow = editableRows.some((r, idx) => {
      const currentFull = getFullSftpPath(getSftpSuffix(r.sftpFileLocation));
      const prev = rowValidationStatus[idx];
      return prev && prev.lastValidatedValue === currentFull && prev.valid === false;
    });

    return (
      <Box className={classes.section}>
        <Box className={classes.titleBar}>
          <Typography>Merchant NACHA Details</Typography>
        </Box>

        {!hasRows && !loading && (
          <Typography className={classes.message} color="textSecondary">
            {emptyMessage}
          </Typography>
        )}

        {loading && (
          <Typography className={classes.message} color="textSecondary">
            Loading merchant NACHA details...
          </Typography>
        )}

        {hasRows && (
          <>
            <TableContainer className={classes.tableWrapper} component={Paper}>
              <Table className={classes.table}>
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.headerCell}>#</TableCell>
                    {nachaColumns.map((column) => (
                      <TableCell
                        className={classes.headerCell}
                        key={column.key}
                      >
                        {column.header[0]}
                        <br />
                        {column.header[1]}
                      </TableCell>
                    ))}
                    <TableCell className={classes.headerCell}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedRows.map((row, index) => {
                    const rowIndex = startIndex + index;
                    const rowKey = getRowKey(row, rowIndex);
                    return (
                      <NachaMerchantTableRow
                        key={rowKey}
                        row={row}
                        rowIndex={rowIndex}
                        columns={nachaColumns}
                        editableMap={editableMap[rowIndex] || {}}
                        rowErrors={rowErrors[rowIndex] || {}}
                        isEditing={Boolean(editingRows[rowKey])}
                        onEdit={handleEditClick}
                        onSave={handleRowSave}
                        onCancel={handleRowCancel}
                        onChange={handleChange}
                            onBlur={(ri) => {
                              // only trigger validation when SFTP field blurs
                              const currentSuffix = getSftpSuffix(
                                editableRows[ri]?.sftpFileLocation || ""
                              );
                              if (!currentSuffix.trim()) {
                                setTouchedFields((currentTouched) => ({
                                  ...currentTouched,
                                  [ri]: {
                                    ...(currentTouched[ri] || {}),
                                    sftpFileLocation: true,
                                  },
                                }));
                                setRowErrors((currentErrors) => ({
                                  ...currentErrors,
                                  [ri]: {
                                    ...(currentErrors[ri] || {}),
                                    sftpFileLocation: validateField(
                                      "sftpFileLocation",
                                      currentSuffix
                                    ),
                                  },
                                }));
                                setRowValidationStatus((current) => {
                                  const next = { ...current };
                                  delete next[ri];
                                  return next;
                                });
                                return;
                              }
                              const currentFull = getFullSftpPath(currentSuffix);
                              const lastValidated = rowValidationStatus[ri]?.lastValidatedValue;
                              if (currentFull && currentFull === lastValidated) {
                                return;
                              }
                              // trigger one-time validation for changed value
                              validateRowSftp(ri);
                            }}
                            rowValidation={rowValidationStatus[rowIndex]}
                        readOnlyColumns={readOnlyColumns}
                        touchedFields={touchedFields[rowIndex] || {}}
                        classes={classes}
                      />
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <Box className={classes.paginationRow}>
              <Typography className={classes.paginationInfo}>
                Showing {startIndex + 1}-{Math.min(endIndex, editableRows.length)} of {editableRows.length}
              </Typography>
              <Box className={classes.paginationButtons}>
                <Typography className={classes.paginationInfo}>Rows per page:</Typography>
                <TextField
                  className={classes.rowsPerPageSelect}
                  onChange={(event) => setPageSize(Number(event.target.value) || 10)}
                  select
                  SelectProps={{ native: true }}
                  size="small"
                  value={pageSize}
                  variant="outlined"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </TextField>
                <Button
                  color="primary"
                  disabled={safePage === 1}
                  onClick={() => setPage((currentPage) => Math.max(1, currentPage - 1))}
                  size="small"
                  variant="outlined"
                >
                  Previous
                </Button>
                <Button
                  color="primary"
                  disabled={safePage === totalPages}
                  onClick={() => setPage((currentPage) => Math.min(totalPages, currentPage + 1))}
                  size="small"
                  variant="outlined"
                >
                  Next
                </Button>
              </Box>
            </Box>
            <Box className={classes.actionRow}>
              <Button
                color="primary"
                disabled={saving || loading || validatingAll || hasValidationFailedNow}
                onClick={handleSave}
                size="small"
                variant="contained"
              >
                {(saving || validatingAll) && (
                  <CircularProgress
                    className={classes.progress}
                    size={16}
                    thickness={5}
                  />
                )}
                {validatingAll ? "Validating..." : saveLabel || "Save NACHA Details"}
              </Button>
            </Box>
          </>
        )}

        {canRetry && !hasRows && !loading && (
          <Box className={classes.actionRow}>
            <Button
              color="primary"
              disabled={saving || loading}
              onClick={onRetry}
              variant="contained"
            >
              Retry
            </Button>
          </Box>
        )}
      </Box>
    );
  }

  NachaMerchantTable.propTypes = {
    rows: PropTypes.arrayOf(PropTypes.object),
    canRetry: PropTypes.bool,
    loading: PropTypes.bool,
    saving: PropTypes.bool,
    onRetry: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    merchantId: PropTypes.string,
    saveLabel: PropTypes.string,
    title: PropTypes.string,
    message: PropTypes.string,
  };

  NachaMerchantTable.defaultProps = {
    rows: [],
    canRetry: false,
    loading: false,
    saving: false,
    merchantId: "",
    saveLabel: "Save NACHA Details",
    title: "Merchant NACHA Details",
    message: "",
  };

  export default NachaMerchantTable;
